import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaTimes,
  FaDownload,
  FaFileInvoice,
  FaCheckSquare,
  FaRegSquare,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const VATReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass = "bg-gray-200 hover:bg-gray-300 text-gray-800";

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "";
  const storedBranchType = localStorage.getItem("branchType") || "HO";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": storedBranchId,
    "x-branch": storedBranchType,
    "x-userid": userId,
  };

  const today = new Date().toISOString().split("T")[0];

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [branchType, setBranchType] = useState(storedBranchType || "HO");
  const [branchId, setBranchId] = useState(storedBranchId || "8");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [isVatDisapproved, setIsVatDisapproved] = useState(false);

  const [branchList, setBranchList] = useState([]);
  const [data, setData] = useState([]);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch branch list
  useEffect(() => {
    setLoading(true);

    axios
      .get("http://nissi-india.com/api/branches_VATrpt", {
        params: {
          branchtype: branchType,
          branchid: storedBranchId || 8,
        },
        headers: commonHeaders,
      })
      .then((res) => {
        const branches = res.data?.data || [];
        setBranchList(branches);

        if (branches.length > 0) {
          setBranchId(String(branches[0].fld_Branchid || storedBranchId || 8));
        }
      })
      .catch((err) => {
        console.error("Branch fetch failed", err);
        toast.error("Failed to load branches");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [branchType]);

  // Fetch VAT listing
  const handleSearch = () => {
    if (!fromDate || !toDate || !branchId) {
      toast.warning("Please select From Date, To Date and Branch");
      return;
    }

    setLoading(true);

    axios
      .get("http://nissi-india.com/api/vat_rpt", {
        params: {
          branchid: branchId,
          branchtype: branchType,
          isvatdis: isVatDisapproved,
          dtfrom: fromDate,
          dtto: toDate,
        },
        headers: commonHeaders,
      })
      .then((res) => {
        const rows = res.data?.data || [];
        setData(rows);
        setCurrentPage(1);
        toast.success(`Loaded ${rows.length} records`);
      })
      .catch((err) => {
        console.error("VAT listing fetch failed", err);
        toast.error("Failed to fetch VAT listing");
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Print
  const handlePrint = (fld_vat) => {
    const query = new URLSearchParams({
      branchid: branchId,
      branchtype: branchType,
      isvatdis: isVatDisapproved,
      dtfrom: fromDate,
      dtto: toDate,
      fld_vat: fld_vat || "",
    }).toString();

    window.open(`http://nissi-india.com/api/vat_rpt_print?${query}`, "_blank");
  };

  // Export placeholder
  const handleExport = () => {
    toast.info("Export API not connected yet");
  };

  const clearFilters = () => {
    setFromDate(today);
    setToDate(today);
    setIsVatDisapproved(false);
    setData([]);
    setCurrentPage(1);

    if (branchList.length > 0) {
      setBranchId(String(branchList[0].fld_Branchid || storedBranchId || 8));
    } else {
      setBranchId(storedBranchId || "8");
    }

    setBranchType(storedBranchType || "HO");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  const totals = useMemo(() => {
    return data.reduce(
      (acc, row) => {
        acc.accAmount += Number(row.AccAmount || 0);
        acc.vatAmount += Number(row.VAT || 0);
        return acc;
      },
      {
        accAmount: 0,
        vatAmount: 0,
      }
    );
  }, [data]);

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  }, [data, currentPage, rowsPerPage]);

  const startRecord =
    data.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, data.length);

  return (
    <LayoutWrapper>
      <HeaderBar title="VAT REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">VAT Report</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track and manage Value Added Tax records
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleExport}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
              disabled={data.length === 0}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">
                  From Date *
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  To Date *
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Branch *
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  {branchList.map((b) => (
                    <option key={b.fld_Branchid} value={b.fld_Branchid}>
                      {b.fld_BranchName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  VAT Status
                </label>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setIsVatDisapproved((prev) => !prev)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    {isVatDisapproved ? (
                      <FaCheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <FaRegSquare className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-sm">VAT Disapproved</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>
            </div>
          </div>
        )}

        {/* Results */}
        <div>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} records)`}
            </h2>

            {data.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-sm text-gray-500">
                  Total VAT:{" "}
                  <span className="font-semibold">
                    {formatCurrency(totals.vatAmount)}
                  </span>
                </span>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Rows:</label>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border rounded bg-white text-sm text-gray-800"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Input VAT
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Accessible Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        VAT Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((row, index) => {
                      const serialNo =
                        (currentPage - 1) * rowsPerPage + index + 1;

                      return (
                        <tr
                          key={`${row.fld_branchname}-${row.fld_vat}-${serialNo}`}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {serialNo}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {row.fld_branchname || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {row.fld_vat || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(row.AccAmount)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(row.VAT)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handlePrint(row.fld_vat)}
                              className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                              title="Print Report"
                            >
                              <FaPrint className="w-4 h-4" />
                              Print
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot className="bg-gray-100">
                    <tr>
                      <td
                        colSpan="3"
                        className="px-4 py-3 text-right text-sm font-semibold text-gray-900"
                      >
                        Totals
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.accAmount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.vatAmount)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
                <div className="text-sm text-gray-500">
                  Showing {startRecord} to {endRecord} of {data.length} entries
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded border flex items-center gap-2 ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <FaChevronLeft className="w-3 h-3" />
                    Prev
                  </button>

                  <span className="px-3 py-2 text-sm font-medium">
                    Page {currentPage} of {totalPages || 1}
                  </span>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages || 1))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                    className={`px-3 py-2 rounded border flex items-center gap-2 ${
                      currentPage === totalPages || totalPages === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    Next
                    <FaChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <FaFileInvoice className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No VAT records
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default VATReport;